/**
 * Top-level UI state for AIWARI client.
 *
 * Stage flow: splash → faction-select → character-creator → world.
 * Persisted character data (faction + class + name) goes into localStorage
 * so the player doesn't re-pick on every refresh. Real persistence happens
 * server-side once they enter the world.
 */

import { create } from 'zustand';
import type { ClassId } from '../data/classes.js';
import type { Side } from '../data/factions.js';
import type { Faction, RaceId } from '../data/wow-races.js';

/**
 * R232q20+: WotLK 3.3.5a-style character creation flow.
 * splash → realm-select → character-roster → character-creator → world.
 *
 * Removed (no callers): 'side-select', 'subfaction-select', 'class-select',
 * 'wow-create' — those were vestigial intermediate stages from earlier
 * iterations. The unified character-creator now handles race / gender /
 * class / customization / name on a single screen.
 */
// R232q71: 'splash' deprecated — kept for back-compat but App routes splash → auth.
//          'auth' is the new entry point; flow: auth (если userId нет) → realm-select →
//          character-roster → character-creator → world.
export type Stage = 'splash' | 'auth' | 'realm-select' | 'character-roster' | 'character-creator' | 'world';

// R232q5r: per-realm shards. 3 active realms: PvE / PvP-PvE / Hardcore.
export type RealmId = 'eldran' | 'sharpcrest' | 'reserved-3';

export interface RealmConfig {
  id: RealmId;
  name: string;
  rate: string;
  type: 'PvE' | 'PvP/PvE' | 'Hardcore';
  description: string;
  status: 'live' | 'soon' | 'reserved';
  /** R232q69: hardcore realm — character permadeath on first death. */
  hardcore?: boolean;
  /** R232q69: pseudo-online range for activity counter (random per-hour). */
  onlineRange: { min: number; max: number };
}

export const REALMS: RealmConfig[] = [
  {
    id: 'eldran',
    name: 'Эльдран',
    rate: '×1',
    type: 'PvE',
    description: 'Тихий мир для тех, кто ценит ритм неторопливого пути. Низкая смертность, классическая прогрессия.',
    status: 'live',
    onlineRange: { min: 50, max: 150 },
  },
  {
    id: 'sharpcrest',
    name: 'Острый Гребень',
    rate: '×3',
    type: 'PvP/PvE',
    description: 'Тройной опыт и открытое PvP в спорных зонах. Кровь оживляет долины.',
    status: 'live',
    onlineRange: { min: 100, max: 200 },
  },
  {
    id: 'reserved-3',
    name: 'Бессмертник',
    rate: '×1.5',
    type: 'Hardcore',
    description: 'Один шанс. Одна смерть — окончательная. Полтора опыта и репутации, ставка высока.',
    status: 'live',
    hardcore: true,
    onlineRange: { min: 50, max: 100 },
  },
];

/** R232q69: deterministic per-hour pseudo-online for a realm.
 *  Uses (Date.now() / hour) + realmId hash so every client sees same number,
 *  changes once per hour. Real-server player count adds on top via realmRoom.send. */
export function pseudoOnlineForRealm(realm: RealmConfig): number {
  const hour = Math.floor(Date.now() / (60 * 60 * 1000));
  const hash = hashRealmId(realm.id);
  const seed = (hour * 2654435761 + hash) >>> 0;
  // mulberry32-like deterministic PRNG
  let t = seed + 0x6D2B79F5;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  const norm = ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  const span = realm.onlineRange.max - realm.onlineRange.min;
  return realm.onlineRange.min + Math.floor(norm * (span + 1));
}

function hashRealmId(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = ((h << 5) - h + id.charCodeAt(i)) | 0;
  return h >>> 0;
}

export interface CharacterDraft {
  name: string;
  side: Side | null;
  subFactionId: string | null;
  classId: ClassId | null;
  /** R55+: WoW-style faction (alliance / horde). */
  faction: Faction | null;
  /** R55+: WoW race id. */
  raceId: RaceId | null;
  gender: 'male' | 'female';
  bodyType: 'slim' | 'medium' | 'broad';
  /** R55+: hex string of selected skin tone (was 0..1 number). */
  skinTone: string;
  hairStyle: number;
  hairColour: string;
  /**
   * R232q20+: WotLK 3.3.5a-style customization variant indexes. These are
   * stored 0-based indexes into hardcoded counts (CharSections.dbc has not
   * yet been wired in). Visual swap of the actual rig is OUT OF SCOPE
   * (TODO when DBC parser + per-race sections land); for now the values
   * are persisted to the player record so future builds can render them.
   */
  skinVariant: number;
  faceStyle: number;
  hairColourIdx: number;
  facialHair: number;
}

interface UIStore {
  stage: Stage;
  setStage: (s: Stage) => void;
  draft: CharacterDraft;
  setDraft: (patch: Partial<CharacterDraft>) => void;
  resetDraft: () => void;
  /** R232q5r: per-realm shard. null = legacy default eldran. Persisted in localStorage. */
  realm: RealmId | null;
  setRealm: (id: RealmId | null) => void;
}

const STORAGE_KEY = 'aiwari-character-draft';

const defaultDraft: CharacterDraft = {
  name: '',
  side: null,
  subFactionId: null,
  classId: null,
  faction: null,
  raceId: null,
  gender: 'male',
  bodyType: 'medium',
  skinTone: '#F1C7A8',
  hairStyle: 0,
  hairColour: '#3F2417',
  skinVariant: 0,
  faceStyle: 0,
  hairColourIdx: 0,
  facialHair: 0,
};

function loadDraft(): CharacterDraft {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultDraft;
    const parsed = JSON.parse(raw) as Partial<CharacterDraft>;
    // R55: skinTone migrated from number → hex string. If we still have a
    // number, throw it out so the default hex applies.
    if (typeof parsed.skinTone !== 'string') delete (parsed as { skinTone?: unknown }).skinTone;
    return { ...defaultDraft, ...parsed };
  } catch {
    return defaultDraft;
  }
}

function saveDraft(d: CharacterDraft) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(d));
  } catch {}
}

const REALM_KEY = 'aiwari-realm';

function loadRealm(): RealmId | null {
  try {
    const raw = localStorage.getItem(REALM_KEY);
    if (raw === 'eldran' || raw === 'sharpcrest' || raw === 'reserved-3') return raw;
    return null;
  } catch { return null; }
}

export const useUI = create<UIStore>((set, get) => ({
  stage: 'auth',
  setStage: (stage) => set({ stage }),
  draft: loadDraft(),
  setDraft: (patch) => {
    const next = { ...get().draft, ...patch };
    saveDraft(next);
    set({ draft: next });
  },
  resetDraft: () => {
    saveDraft(defaultDraft);
    set({ draft: defaultDraft });
  },
  realm: loadRealm(),
  setRealm: (id) => {
    try {
      if (id) localStorage.setItem(REALM_KEY, id);
      else localStorage.removeItem(REALM_KEY);
    } catch {}
    set({ realm: id });
  },
}));
